import { Button, Label } from '../ui'
import type { TimesheetReportFilters } from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
  organization_id?: string | null
}

const MONTHS = [
  { value: 1, label: 'Январь' },
  { value: 2, label: 'Февраль' },
  { value: 3, label: 'Март' },
  { value: 4, label: 'Апрель' },
  { value: 5, label: 'Май' },
  { value: 6, label: 'Июнь' },
  { value: 7, label: 'Июль' },
  { value: 8, label: 'Август' },
  { value: 9, label: 'Сентябрь' },
  { value: 10, label: 'Октябрь' },
  { value: 11, label: 'Ноябрь' },
  { value: 12, label: 'Декабрь' },
]

// Generate years from 2020 to current year + 1
const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i).reverse()

interface Position {
  id: string
  name: string
}

interface TimesheetFiltersProps {
  filters: TimesheetReportFilters
  onFiltersChange: (filters: Partial<TimesheetReportFilters>) => void
  onGenerateReport: () => void
  onExport?: () => void
  canExport?: boolean
  organizations?: Organization[]
  departments?: Department[]
  /** Должности из уже сформированного табеля (клиентская фильтрация) */
  positions?: Position[]
  positionId?: string
  onPositionChange?: (positionId: string | undefined) => void
  isLoading?: boolean
}

export function TimesheetFilters({
  filters,
  onFiltersChange,
  onGenerateReport,
  onExport,
  canExport = false,
  organizations = [],
  departments = [],
  positions = [],
  positionId,
  onPositionChange,
  isLoading = false,
}: TimesheetFiltersProps) {
  // Filter departments by selected organization
  const filteredDepartments = filters.organizationId
    ? departments.filter((d) => d.organization_id === filters.organizationId)
    : departments

  const handleClear = () => {
    const today = new Date()
    onFiltersChange({
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      organizationId: undefined,
      departmentId: undefined,
    })
    onPositionChange?.(undefined)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        {/* Year select */}
        <div className="space-y-2">
          <Label htmlFor="year">Год</Label>
          <select
            id="year"
            value={filters.year}
            onChange={(e) => onFiltersChange({ year: parseInt(e.target.value, 10) })}
            className="h-10 w-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Month select */}
        <div className="space-y-2">
          <Label htmlFor="month">Месяц</Label>
          <select
            id="month"
            value={filters.month}
            onChange={(e) => onFiltersChange({ month: parseInt(e.target.value, 10) })}
            className="h-10 w-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Organization select */}
        <div className="space-y-2">
          <Label htmlFor="organization">Организация</Label>
          <select
            id="organization"
            value={filters.organizationId || ''}
            onChange={(e) =>
              onFiltersChange({
                organizationId: e.target.value || undefined,
                departmentId: undefined, // Reset department when org changes
              })
            }
            className="h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Все организации</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        {/* Department select */}
        <div className="space-y-2">
          <Label htmlFor="department">Подразделение</Label>
          <select
            id="department"
            value={filters.departmentId || ''}
            onChange={(e) =>
              onFiltersChange({ departmentId: e.target.value || undefined })
            }
            className="h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Все подразделения</option>
            {filteredDepartments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        {/* Position select — список берётся из сформированного табеля */}
        <div className="space-y-2">
          <Label htmlFor="position">Должность</Label>
          <select
            id="position"
            value={positionId || ''}
            onChange={(e) => onPositionChange?.(e.target.value || undefined)}
            disabled={positions.length === 0}
            className="h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Все должности</option>
            {positions.map((pos) => (
              <option key={pos.id} value={pos.id}>
                {pos.name}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button onClick={onGenerateReport} disabled={isLoading}>
            {isLoading ? 'Загрузка...' : 'Сформировать табель'}
          </Button>
          <Button
            variant="default"
            className="bg-green-600 hover:bg-green-700"
            disabled={!canExport}
            onClick={onExport}
          >
            <span className="mr-2">📊</span>
            Экспорт в Excel
          </Button>
          <Button variant="outline" onClick={handleClear}>
            Сбросить
          </Button>
        </div>
      </div>
    </div>
  )
}
